import type { VirtualClinicAnalysisOperatingNumberCalculation } from '../operating-number/calculation/calculation';
import type { VirtualClinicAnalysisOperatingNumberGroupBy } from '../operating-number/group-by/group-by';
import type { VirtualClinicAnalysisOperatingNumberResultSetOperation } from '../operating-number/result-set-operation/result-set-operation';
import type {
  VirtualClinicAnalysisOperatingNumberBenchmarkRequestPart,
  VirtualClinicAnalysisOperatingNumberRequest,
  VirtualClinicAnalysisOperatingNumberSingleRequestPart,
} from './request';
import type {
  VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation,
  VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation,
} from '../operating-number';
import {
  VirtualClinicAnalysisOperatingNumberGroupByHelper
} from '../operating-number';

export type VirtualClinicAnalysisOperatingNumberRequestStrategy =
  | 'single'
  | 'merged';

export class VirtualClinicAnalysisOperatingNumberRequestHelper {
  public static calculationsToRequest(
    parts: {
      id: string;
      calculation: VirtualClinicAnalysisOperatingNumberCalculation;
      groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
    }[],
    resultSetOperation?: VirtualClinicAnalysisOperatingNumberResultSetOperation,
  ): VirtualClinicAnalysisOperatingNumberRequest[] {
    const resultSetOperationValue =
      this.resultSetOperationToRequestFormat(resultSetOperation);
    const benchmarks = parts.filter(
      (part) => part.calculation.type === 'benchmarking',
    ) as {
      id: string;
      calculation: VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation;
      groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
    }[];

    const singleVirtualClinics = parts.filter(
      (part) => part.calculation.type === 'single-virtual-clinic',
    ) as {
      id: string;
      calculation: VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation;
      groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
    }[];

    const benchmarkRequests = this._benchmarkVirtualClinicCalculationsToRequest(
      benchmarks,
      resultSetOperationValue,
      'merged',
    );
    const singleRequests = this._singleVirtualClinicCalculationsToRequest(
      singleVirtualClinics,
      resultSetOperationValue,
    );

    return [...benchmarkRequests, ...singleRequests];
  }

  private static _singleVirtualClinicCalculationsToRequest(
    parts: {
      id: string;
      calculation: VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation;
      groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
    }[],
    resultSetOperationValue?: { name: string; parameters: string[] },
  ): VirtualClinicAnalysisOperatingNumberRequest[] {
    const partsGroupedByVirtualClinicId: {
      [virtualClinicId: string]: {
        [partId: string]: {
          calculation: VirtualClinicAnalysisOperatingNumberCalculation;
          groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
        };
      };
    } = parts.reduce(
      (previousValue, currentValue) => {
        if (previousValue[currentValue.calculation.virtualClinicId]) {
          previousValue[currentValue.calculation.virtualClinicId][
            currentValue.id
          ] = {
            calculation: currentValue.calculation,
            groupBy:
              VirtualClinicAnalysisOperatingNumberGroupByHelper.toRequestData(
                currentValue.groupBy,
              ),
          };
        } else {
          previousValue[currentValue.calculation.virtualClinicId] = {
            [currentValue.id]: {
              calculation: currentValue.calculation,
              groupBy:
                VirtualClinicAnalysisOperatingNumberGroupByHelper.toRequestData(
                  currentValue.groupBy,
                ),
            },
          };
        }

        return previousValue;
      },
      {} as {
        [virtualClinicId: string]: {
          [partId: string]: {
            calculation: VirtualClinicAnalysisOperatingNumberCalculation;
            groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
          };
        };
      },
    );

    const requestParts = parts.map((part) => {
      const requestPart = part.calculation.toRequestPart();
      return {
        requestPart: requestPart,
        partId: part.id,
      };
    });

    return Object.keys(partsGroupedByVirtualClinicId).map((virtualClinicId) => {
      return Object.keys(
        partsGroupedByVirtualClinicId[virtualClinicId],
      ).reduce(
        (previousValue, currentValue) => {
          const found = requestParts.find((it) => it.partId === currentValue);
          if (!found?.requestPart) {
            return previousValue;
          }

          const requestPart: VirtualClinicAnalysisOperatingNumberSingleRequestPart =
            {
              type: 'VirtualClinicOperatingNumberSingle',
              term: null,
              ...found.requestPart,
              groupBy:
                partsGroupedByVirtualClinicId[virtualClinicId][currentValue]
                  .groupBy,
              virtualClinicId,
            };

          if (resultSetOperationValue) {
            requestPart.resultSetOperation = resultSetOperationValue;
          }

          previousValue[currentValue] = requestPart;

          return previousValue;
        },
        {} as VirtualClinicAnalysisOperatingNumberRequest,
      );
    });
  }

  private static _benchmarkVirtualClinicCalculationsToRequest(
    parts: {
      id: string;
      calculation: VirtualClinicAnalysisOperatingNumberBenchmarkingCalculation;
      groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[];
    }[],
    resultSetOperationValue?: { name: string; parameters: string[] },
    benchmarkRequestStrategy: VirtualClinicAnalysisOperatingNumberRequestStrategy = 'single',
  ): VirtualClinicAnalysisOperatingNumberRequest[] {
    const toRequestPartFnc = (
      calculation: VirtualClinicAnalysisOperatingNumberCalculation,
      groupBy: VirtualClinicAnalysisOperatingNumberGroupBy[],
      resultSetOperationVal?: { name: string; parameters: string[] },
    ):
      | VirtualClinicAnalysisOperatingNumberBenchmarkRequestPart
      | VirtualClinicAnalysisOperatingNumberSingleRequestPart
      | null => {
      const requestPartBase = calculation.toRequestPart();
      if (!requestPartBase) {
        return null;
      }
      const requestPart = {
        ...requestPartBase,
        groupBy:
          VirtualClinicAnalysisOperatingNumberGroupByHelper.toRequestData(
            groupBy,
          ),
      };

      if (resultSetOperationVal) {
        requestPart.resultSetOperation = resultSetOperationVal;
      }

      return requestPart;
    };

    const requestParts = parts.map((part) => {
      const requestPart = toRequestPartFnc(
        part.calculation,
        part.groupBy,
        resultSetOperationValue,
      );
      return { requestPart: requestPart, id: part.id };
    });

    if (benchmarkRequestStrategy === 'single') {
      return [
        requestParts.reduce(
          (previousValue, currentValue) => {
            previousValue[currentValue.id] = currentValue.requestPart;
            return previousValue;
          },
          {} as VirtualClinicAnalysisOperatingNumberRequest,
        ),
      ];
    } else {
      return requestParts.map((part) => {
        const result: VirtualClinicAnalysisOperatingNumberRequest = {};
        result[part.id] = part.requestPart;
        return result;
      });
    }
  }

  /**
   * Returns the result set operation in the format expected by the API.
   * Since the interface now matches the API format directly, this method
   * simply returns the value or null.
   */
  public static resultSetOperationToRequestFormat(
    resultSetOperation: VirtualClinicAnalysisOperatingNumberResultSetOperation | null | undefined,
  ): VirtualClinicAnalysisOperatingNumberResultSetOperation | null {
    return resultSetOperation ?? null;
  }
}
